import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import venueService from '../services/VenueService';
import appointmentService from '../services/AppointmentService';

/**
 * VenueDetailPage component for viewing and managing a specific venue
 * @returns {React.ReactElement} VenueDetailPage component
 */
const VenueDetailPage = () => {
  // Get venue id from URL params
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  
  // State
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    capacity: 1,
    description: '',
    isActive: true
  });
  
  // Activity stats
  const [stats, setStats] = useState({
    upcomingAppointments: 0,
    todayAppointments: 0,
    totalAppointments: 0
  });

  // Check permissions
  const canViewVenue = hasPermission('venues', 'read');
  const canEditVenue = hasPermission('venues', 'write');
  
  // Fetch venue data
  useEffect(() => {
    if (!id || !canViewVenue) {
      return;
    }
    
    const fetchVenue = () => {
      setLoading(true);
      try {
        const venueData = venueService.getVenueById(id);
        if (!venueData) {
          setError('找不到场地');
          return;
        }
        setVenue(venueData);
        setFormData({
          name: venueData.name || '',
          location: venueData.location || '',
          capacity: venueData.capacity || 1,
          description: venueData.description || '',
          isActive: venueData.isActive !== false
        });
        
        // Fetch stats
        const allAppointments = appointmentService.getAllAppointments()
          .filter(appt => appt.venueId === id);
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const upcomingAppointments = allAppointments.filter(appt => 
          (appt.status === 'scheduled' || appt.status === 'confirmed') && 
          new Date(appt.appointmentTime) > new Date()
        );
        
        const todayAppointments = allAppointments.filter(appt => {
          const apptDate = new Date(appt.appointmentTime);
          apptDate.setHours(0, 0, 0, 0);
          return apptDate.getTime() === today.getTime();
        });
        
        setStats({
          upcomingAppointments: upcomingAppointments.length,
          todayAppointments: todayAppointments.length,
          totalAppointments: allAppointments.length
        });
        
        setError(null);
      } catch (err) {
        console.error('Error fetching venue:', err);
        setError('获取场地数据失败');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVenue();
  }, [id, canViewVenue]);
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              name === 'capacity' ? parseInt(value) : value
    }));
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    try {
      venueService.updateVenue(id, formData);
      
      // Refresh venue data
      const refreshedVenue = venueService.getVenueById(id);
      setVenue(refreshedVenue);
      
      // Exit edit mode
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating venue:', err);
      alert('更新场地信息失败');
    }
  };
  
  // Handle delete
  const handleDelete = () => {
    if (window.confirm('确定要删除此场地吗？此操作无法撤销，且可能影响关联的预约。')) {
      try {
        venueService.deleteVenue(id);
        navigate('/venues');
      } catch (err) {
        console.error('Error deleting venue:', err);
        alert('删除场地失败');
      }
    }
  };
  
  // If there's an error finding the venue
  if (error === '找不到场地') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <div>
            <h2 className="text-3xl font-extrabold text-gray-900">
              场地未找到
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              您尝试访问的场地不存在或已被删除
            </p>
            <div className="mt-5">
              <Link
                to="/venues"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                返回场地列表
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // If user doesn't have permission
  if (!canViewVenue) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <div>
            <svg className="mx-auto h-12 w-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="mt-2 text-3xl font-extrabold text-gray-900">
              权限不足
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              您没有查看场地详情的权限
            </p>
            <div className="mt-5">
              <Link
                to="/"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                返回首页
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Back Button and Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/venues')}
            className="mr-4 inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            返回列表
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {loading ? '加载场地信息...' : '场地详情'}
          </h1>
        </div>
        {canEditVenue && !isEditing && (
          <div className="mt-4 sm:mt-0 space-x-2">
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              编辑信息
            </button>
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              删除场地
            </button>
          </div>
        )}
      </div>
      
      {/* Error Display */}
      {error && error !== '找不到场地' && (
        <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center my-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      ) : isEditing ? (
        /* Edit Form */
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              编辑场地信息
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              修改场地的详细信息
            </p>
          </div>
          <div className="border-t border-gray-200">
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-6 gap-6">
                {/* Name */}
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    场地名称 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                {/* Location */}
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                    位置
                  </label>
                  <input
                    type="text"
                    name="location"
                    id="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                {/* Capacity */}
                <div className="col-span-6 sm:col-span-3">
                  <label htmlFor="capacity" className="block text-sm font-medium text-gray-700">
                    容量 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="capacity"
                    id="capacity"
                    min="1"
                    max="100"
                    value={formData.capacity}
                    onChange={handleInputChange}
                    required
                    className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                
                {/* Active Status */}
                <div className="col-span-6 sm:col-span-3">
                  <div className="flex items-center h-full mt-8">
                    <div className="flex items-center">
                      <input
                        id="isActive"
                        name="isActive"
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                        场地可用状态
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* Description */}
                <div className="col-span-6">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    描述
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleInputChange}
                    className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="场地的设施、环境、用途等相关信息"
                  ></textarea>
                </div>
              </div>
              
              <div className="pt-5 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  保存
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : venue ? (
        /* View Venue Details */
        <div className="space-y-6">
          {/* Venue Details */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {venue.name}
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  场地详细信息
                </p>
              </div>
              <div>
                {venue.isActive ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    可用
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    不可用
                  </span>
                )}
              </div>
            </div>
            <div className="border-t border-gray-200">
              <dl>
                {venue.location && (
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">位置</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {venue.location}
                    </dd>
                  </div>
                )}
                
                <div className={`${venue.location ? 'bg-white' : 'bg-gray-50'} px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6`}>
                  <dt className="text-sm font-medium text-gray-500">容量</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {venue.capacity} 人
                  </dd>
                </div>
                
                {venue.description && (
                  <div className={`${venue.location ? 'bg-gray-50' : 'bg-white'} px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6`}>
                    <dt className="text-sm font-medium text-gray-500">描述</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 whitespace-pre-line">
                      {venue.description}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
          
          {/* Activity Stats */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                预约统计
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                此场地的预约使用情况
              </p>
            </div>
            <div className="border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3">
                <div className="px-4 py-5 bg-white sm:px-6 text-center border-r border-gray-200">
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    今日预约
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-green-600">
                    {stats.todayAppointments}
                  </dd>
                </div>
                <div className="px-4 py-5 bg-white sm:px-6 text-center border-r border-gray-200">
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    即将到来的预约
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-indigo-600">
                    {stats.upcomingAppointments}
                  </dd>
                </div>
                <div className="px-4 py-5 bg-white sm:px-6 text-center">
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    预约总数
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-blue-600">
                    {stats.totalAppointments}
                  </dd>
                </div>
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex space-x-4">
            <Link
              to={`/appointments?venueId=${id}`}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              查看此场地的预约
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default VenueDetailPage;